import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Info, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { flexiBenefitsService } from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { DsButton } from "../../../design-system";
import AdminFlexiOptInsPanel from "./AdminFlexiOptInsPanel";

const ACTIONABLE = new Set(["SUBMITTED", "CLARIFICATION", "PARTIAL"]);

const STATUS_FILTERS = [
  { key: "actionable", label: "Needs action", match: (s) => ACTIONABLE.has(s) },
  { key: "APPROVED", label: "Approved", match: (s) => s === "APPROVED" },
  { key: "PAID", label: "Paid", match: (s) => s === "PAID" },
  { key: "REJECTED", label: "Rejected", match: (s) => s === "REJECTED" },
  { key: "all", label: "All", match: () => true },
];

function defaultFinancialYear() {
  const d = new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 4 ? y : y - 1;
  const end = start + 1;
  return `${start}-${String(end).slice(-2)}`;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function metricCounts(claims) {
  const n = {
    pending: 0,
    query: 0,
    approved: 0,
    paymentPending: 0,
    paid: 0,
    rejected: 0,
    claimed: 0,
    approvedAmt: 0,
    paidAmt: 0,
  };
  for (const c of claims) {
    const st = String(c.status || "").toUpperCase();
    const claimAmt = Number(c.claimAmount) || 0;
    const appr = Number(c.approvedAmount) || 0;
    n.claimed += claimAmt;
    if (st === "SUBMITTED" || st === "PARTIAL") n.pending += 1;
    if (st === "CLARIFICATION") n.query += 1;
    if (st === "APPROVED") {
      n.approved += 1;
      n.paymentPending += 1;
      n.approvedAmt += appr || claimAmt;
    }
    if (st === "PAID") {
      n.paid += 1;
      n.paidAmt += appr || claimAmt;
    }
    if (st === "REJECTED") n.rejected += 1;
  }
  return n;
}

/**
 * Admin Reimbursement Control Center — Flexi claims dashboard + decisions + FY-end settle.
 */
export default function AdminFlexiClaimsPanel({
  darkMode = false,
  employeeId = null,
  payMonth = null,
}) {
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canOptInsView =
    isAdmin ||
    hasScreenAccess("payroll", "pay_act_reimb_optins", "view") ||
    hasScreenAccess("payroll", "flexi_benefits", "view");
  const canManageOptIns =
    isAdmin ||
    hasScreenAccess("payroll", "pay_act_reimb_optins", "create") ||
    hasScreenAccess("payroll", "pay_act_reimb_optins", "edit") ||
    hasScreenAccess("payroll", "flexi_benefits", "edit");
  const canClaimsView =
    isAdmin ||
    hasScreenAccess("payroll", "pay_act_reimb_claims", "view") ||
    hasScreenAccess("payroll", "flexi_benefits", "view") ||
    hasScreenAccess("payroll", "pay_act_reimbursement", "view");
  const canApprove =
    isAdmin ||
    hasScreenAccess("payroll", "pay_act_reimb_claims", "approve") ||
    hasScreenAccess("payroll", "flexi_benefits", "approve");
  const canLedgerView =
    isAdmin ||
    hasScreenAccess("payroll", "pay_act_reimb_ledger", "view") ||
    hasScreenAccess("payroll", "flexi_benefits", "view");
  const canLedgerExport =
    isAdmin ||
    hasScreenAccess("payroll", "pay_act_reimb_ledger", "export") ||
    canLedgerView;
  const canYearEndView =
    isAdmin ||
    hasScreenAccess("payroll", "pay_act_reimb_year_end", "view") ||
    hasScreenAccess("payroll", "flexi_benefits", "view");
  const canYearEndRun =
    isAdmin ||
    hasScreenAccess("payroll", "pay_act_reimb_year_end", "edit") ||
    hasScreenAccess("payroll", "flexi_benefits", "edit") ||
    hasScreenAccess("payroll", "flexi_benefits", "approve");
  const canView =
    canOptInsView || canClaimsView || canLedgerView || canYearEndView;

  const tabs = useMemo(
    () =>
      [
        canOptInsView ? { id: "optins", label: "Opt-ins" } : null,
        canClaimsView ? { id: "claims", label: "Claims" } : null,
        canLedgerView ? { id: "ledger", label: "Ledger" } : null,
        canYearEndView ? { id: "year_end", label: "Year-end" } : null,
      ].filter(Boolean),
    [canOptInsView, canClaimsView, canLedgerView, canYearEndView]
  );

  const [loading, setLoading] = useState(true);
  const [allClaims, setAllClaims] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [settleBusy, setSettleBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("actionable");
  const [componentFilter, setComponentFilter] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [paymentMode, setPaymentMode] = useState("PAYROLL");
  const [defaultPayChannel, setDefaultPayChannel] = useState("PAYROLL");
  const [adminView, setAdminView] = useState(() => tabs[0]?.id || "claims");
  const [financialYear, setFinancialYear] = useState(defaultFinancialYear);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [yearEnd, setYearEnd] = useState(null);
  const [yearEndLoading, setYearEndLoading] = useState(false);

  useEffect(() => {
    if (!tabs.some((t) => t.id === adminView)) {
      setAdminView(tabs[0]?.id || "claims");
    }
  }, [tabs, adminView]);

  const load = useCallback(async () => {
    if (!canClaimsView) {
      setLoading(false);
      setAllClaims([]);
      return;
    }
    setLoading(true);
    try {
      const res = await flexiBenefitsService.listClaims({
        ...(employeeId ? { employeeId } : {}),
      });
      setAllClaims(res?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load claims");
      setAllClaims([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId, canClaimsView]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    flexiBenefitsService.getAccess().then((res) => {
      const mode = res?.data?.reimbursementPaymentMode || "PAYROLL";
      setPaymentMode(mode);
      setDefaultPayChannel(mode === "OUT_OF_CYCLE" ? "OUT_OF_CYCLE" : "PAYROLL");
    }).catch(() => {});
  }, []);

  const loadLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const res = await flexiBenefitsService.listLedger({
        financialYear,
        ...(employeeId ? { employeeId } : {}),
        limit: 500,
      });
      setLedgerRows(res?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load ledger");
      setLedgerRows([]);
    } finally {
      setLedgerLoading(false);
    }
  }, [financialYear, employeeId]);

  const loadYearEnd = useCallback(async () => {
    setYearEndLoading(true);
    try {
      const res = await flexiBenefitsService.getYearEndSummary({ financialYear });
      setYearEnd(res?.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load year-end summary");
      setYearEnd(null);
    } finally {
      setYearEndLoading(false);
    }
  }, [financialYear]);

  useEffect(() => {
    if (adminView === "ledger") loadLedger();
    if (adminView === "year_end") loadYearEnd();
  }, [adminView, loadLedger, loadYearEnd]);

  const metrics = useMemo(() => metricCounts(allClaims), [allClaims]);

  const componentOptions = useMemo(() => {
    const set = new Set();
    allClaims.forEach((c) => {
      if (c.componentCode) set.add(String(c.componentCode));
    });
    return Array.from(set).sort();
  }, [allClaims]);

  const claims = useMemo(() => {
    const filterDef =
      STATUS_FILTERS.find((f) => f.key === statusFilter) || STATUS_FILTERS[0];
    return allClaims.filter((c) => {
      const st = String(c.status || "").toUpperCase();
      if (!filterDef.match(st)) return false;
      if (componentFilter && String(c.componentCode) !== componentFilter) {
        return false;
      }
      return true;
    });
  }, [allClaims, statusFilter, componentFilter]);

  const decide = async (id, decision, approvedAmount, extra = {}) => {
    if (!canApprove) {
      toast.error("No permission to approve Flexi claims");
      return;
    }
    setBusyId(id);
    try {
      await flexiBenefitsService.decideClaim(id, {
        decision,
        approvedAmount,
        paymentChannel: extra.paymentChannel,
        remarks: extra.remarks,
      });
      toast.success(`Claim ${decision.toLowerCase()}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Decision failed");
    } finally {
      setBusyId(null);
    }
  };

  const runFyEndSettle = async () => {
    if (!canYearEndRun) {
      toast.error("No permission to settle Flexi");
      return;
    }
    const ym =
      payMonth ||
      (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      })();
    if (
      !window.confirm(
        `Settle unclaimed Flexi declared remainder for pay month ${ym}?`
      )
    ) {
      return;
    }
    setSettleBusy(true);
    try {
      const res = await flexiBenefitsService.settleFyEnd({ payMonth: ym });
      toast.success(
        res?.message ||
          `FY-end settle done (${res?.data?.settled ?? res?.data?.count ?? 0} item(s))`
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "FY-end settle failed");
    } finally {
      setSettleBusy(false);
    }
  };

  const border = "border-[hsl(var(--ds-border))]";
  const muted = "text-[hsl(var(--ds-muted-fg))]";
  const card = darkMode
    ? "bg-[hsl(var(--ds-surface))]"
    : "bg-[hsl(var(--ds-surface))]";

  if (!canView) {
    return (
      <p className={`text-sm ${muted}`}>
        You do not have access to the Reimbursement Control Center.
      </p>
    );
  }

  if (loading && canClaimsView && adminView === "claims") {
    return (
      <div className={`flex h-24 items-center justify-center gap-2 text-sm ${muted}`}>
        <Loader2 className="animate-spin" size={16} /> Loading reimbursement claims…
      </div>
    );
  }

  const monthNum = payMonth ? Number(String(payMonth).slice(5, 7)) : null;
  const showMarchSettle = monthNum === 3 || monthNum === null;

  const metricTiles = [
    { label: "Pending approval", value: metrics.pending, filter: "actionable" },
    { label: "Query raised", value: metrics.query, filter: "actionable" },
    { label: "Approved (unpaid)", value: metrics.approved, filter: "APPROVED" },
    { label: "Paid", value: metrics.paid, filter: "PAID" },
    { label: "Rejected", value: metrics.rejected, filter: "REJECTED" },
  ];

  return (
    <div className={`space-y-3 rounded-xl border p-4 ${border} ${card}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <div>
            <p className="text-sm font-semibold">Reimbursement Control Center</p>
          </div>
          <button
            type="button"
            onClick={() => setShowInfo((v) => !v)}
            className={`mt-0.5 shrink-0 rounded-full p-1 ${muted} hover:bg-[hsl(var(--ds-muted)/0.35)]`}
            aria-label="Reimbursement control center information"
          >
            <Info size={15} strokeWidth={1.75} />
          </button>
        </div>
        {canYearEndRun && showMarchSettle ? (
          <DsButton
            type="button"
            variant="outline"
            size="sm"
            disabled={settleBusy}
            onClick={runFyEndSettle}
          >
            {settleBusy ? "Settling…" : "March / FY-end settle"}
          </DsButton>
        ) : null}
      </div>

      {showInfo && (
        <div
          className={`rounded-lg border px-3 py-2 text-[11px] leading-relaxed ${border} ${muted}`}
        >
          <p className="font-medium text-[hsl(var(--ds-fg))]">How to use</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li>
              <strong>Opt-ins</strong> — company-wide list; users with Opt-ins access can
              opt-in on behalf, change amount, or revoke with mandatory remarks (audited
              HR override).
            </li>
            <li>
              <strong>Claims</strong> — approve / partial / reject / query. Payment follows
              Payroll → Settings → Reimbursements; multi-stage roles use Claim approval workflow.
            </li>
            <li>
              <strong>Ledger / Year-end</strong> — posting history and FY-end lapse settle.
            </li>
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setAdminView(t.id)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              adminView === t.id
                ? "bg-[hsl(var(--ds-primary))] text-white"
                : `border ${border} hover:bg-[hsl(var(--ds-muted)/0.25)]`
            }`}
          >
            {t.label}
          </button>
        ))}
        {adminView !== "claims" ? (
          <input
            type="text"
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            placeholder="FY e.g. 2026-27"
            className={`ml-auto rounded-lg border px-2 py-1 text-xs ${border}`}
          />
        ) : null}
      </div>

      {adminView === "optins" && (
        <AdminFlexiOptInsPanel
          darkMode={darkMode}
          canAct={canManageOptIns}
          financialYear={financialYear}
        />
      )}

      {adminView === "claims" && (
      <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {metricTiles.map((t) => (
          <button
            key={t.label}
            type="button"
            onClick={() => setStatusFilter(t.filter)}
            className={`rounded-lg border px-2.5 py-2 text-left transition ${border} ${
              statusFilter === t.filter
                ? "ring-2 ring-[hsl(var(--ds-primary)/0.45)]"
                : "hover:bg-[hsl(var(--ds-muted)/0.25)]"
            }`}
          >
            <p className={`text-[10px] uppercase tracking-wide ${muted}`}>{t.label}</p>
            <p className="text-lg font-semibold tabular-nums">{t.value}</p>
          </button>
        ))}
      </div>

      <div className={`flex flex-wrap gap-3 text-[11px] ${muted}`}>
        <span>
          Claimed total: ₹{metrics.claimed.toLocaleString("en-IN")}
        </span>
        <span>
          Approved unpaid: ₹{metrics.approvedAmt.toLocaleString("en-IN")}
        </span>
        <span>Paid: ₹{metrics.paidAmt.toLocaleString("en-IN")}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={`rounded-lg border px-2 py-1.5 text-xs ${border} bg-transparent`}
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        <select
          value={componentFilter}
          onChange={(e) => setComponentFilter(e.target.value)}
          className={`rounded-lg border px-2 py-1.5 text-xs ${border} bg-transparent`}
        >
          <option value="">All components</option>
          {componentOptions.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </div>

      {!claims.length ? (
        <p className={`text-sm ${muted}`}>No claims for this filter.</p>
      ) : (
        <div className="space-y-2">
          {claims.map((c) => {
            const actionable = ACTIONABLE.has(String(c.status || "").toUpperCase());
            const wf = c.workflow || c.detailsJson?.workflow;
            const isFinalStep =
              !wf?.totalSteps || !wf?.currentStep || wf.currentStep >= wf.totalSteps;
            return (
              <div key={c.id} className={`rounded-lg border px-3 py-2 text-sm ${border}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium">{c.componentCode}</span> · Emp #
                    {c.employeeId} · ₹{c.claimAmount}
                    {c.approvedAmount != null ? (
                      <span className={muted}> · appr ₹{c.approvedAmount}</span>
                    ) : null}{" "}
                    · {c.status}
                    {wf?.pendingLabel ? (
                      <span className={muted}> · Step: {wf.pendingLabel}</span>
                    ) : null}
                    {c.paymentChannel ? (
                      <span className={muted}> · Pay via {c.paymentChannel}</span>
                    ) : null}
                    {c.claimPeriodFrom ? (
                      <span className={`ml-1 ${muted}`}>
                        ({c.claimPeriodFrom} → {c.claimPeriodTo || "—"})
                      </span>
                    ) : null}
                  </div>
                  {canApprove && actionable ? (
                    <div className="flex flex-wrap items-center gap-1">
                      {paymentMode === "BOTH" && isFinalStep ? (
                        <select
                          className={`rounded border px-1 py-0.5 text-[10px] ${border}`}
                          value={defaultPayChannel}
                          onChange={(e) => setDefaultPayChannel(e.target.value)}
                        >
                          <option value="PAYROLL">Payroll</option>
                          <option value="OUT_OF_CYCLE">Out-of-cycle</option>
                        </select>
                      ) : null}
                      <button
                        type="button"
                        disabled={busyId === c.id}
                        onClick={() =>
                          decide(c.id, "APPROVED", c.claimAmount, {
                            paymentChannel:
                              paymentMode === "BOTH" && isFinalStep
                                ? defaultPayChannel
                                : undefined,
                          })
                        }
                        className="rounded bg-[hsl(var(--ds-success))] px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        disabled={busyId === c.id}
                        onClick={() => {
                          const amt = window.prompt(
                            "Partial approved amount",
                            String(c.claimAmount)
                          );
                          if (amt == null) return;
                          decide(c.id, "PARTIAL", Number(amt), {
                            paymentChannel:
                              paymentMode === "BOTH" && isFinalStep
                                ? defaultPayChannel
                                : undefined,
                          });
                        }}
                        className="rounded bg-[hsl(var(--ds-warning))] px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50"
                      >
                        Partial
                      </button>
                      <button
                        type="button"
                        disabled={busyId === c.id}
                        onClick={() => {
                          const remarks = window.prompt("Query / clarification for employee");
                          if (remarks == null) return;
                          decide(c.id, "CLARIFICATION", c.claimAmount, { remarks });
                        }}
                        className="rounded bg-[hsl(var(--ds-info))] px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50"
                      >
                        Query
                      </button>
                      <button
                        type="button"
                        disabled={busyId === c.id}
                        onClick={() => decide(c.id, "REJECTED", 0)}
                        className="rounded bg-[hsl(var(--ds-danger))] px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  ) : null}
                  {canApprove && ["APPROVED", "PARTIAL"].includes(String(c.status)) ? (
                    <button
                      type="button"
                      disabled={busyId === c.id}
                      onClick={async () => {
                        if (
                          !window.confirm(
                            "Mark provisional payment (bill can be submitted later)?"
                          )
                        ) {
                          return;
                        }
                        setBusyId(c.id);
                        try {
                          await flexiBenefitsService.payProvisional(c.id, {
                            payMonth,
                            paidAmount: c.approvedAmount || c.claimAmount,
                          });
                          toast.success("Provisional payment recorded");
                          await load();
                        } catch (err) {
                          toast.error(err?.response?.data?.message || "Provisional pay failed");
                        } finally {
                          setBusyId(null);
                        }
                      }}
                      className="rounded border px-2 py-1 text-[11px] font-medium disabled:opacity-50"
                    >
                      Provisional pay
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}

      {adminView === "ledger" && (
        <div className="space-y-2">
          {ledgerLoading ? (
            <div className={`flex h-20 items-center justify-center gap-2 text-sm ${muted}`}>
              <Loader2 className="animate-spin" size={16} /> Loading ledger…
            </div>
          ) : !ledgerRows.length ? (
            <p className={`text-sm ${muted}`}>No ledger entries for {financialYear}.</p>
          ) : (
            <div className={`max-h-72 overflow-auto rounded-lg border ${border}`}>
              <table className="min-w-full text-xs">
                <thead className="sticky top-0 bg-[hsl(var(--ds-muted)/0.35)] text-left">
                  <tr>
                    <th className="px-2 py-1.5">Date</th>
                    <th className="px-2 py-1.5">Emp</th>
                    <th className="px-2 py-1.5">Component</th>
                    <th className="px-2 py-1.5">Type</th>
                    <th className="px-2 py-1.5 text-right">Amount</th>
                    <th className="px-2 py-1.5">Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerRows.map((r) => (
                    <tr key={r.id} className={`border-t ${border}`}>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {r.createdAt ? String(r.createdAt).slice(0, 10) : "—"}
                      </td>
                      <td className="px-2 py-1.5">#{r.employeeId}</td>
                      <td className="px-2 py-1.5">{r.componentCode}</td>
                      <td className="px-2 py-1.5">{r.entryType}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.amount}</td>
                      <td className="px-2 py-1.5">{r.reference || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {adminView === "year_end" && (
        <div className="space-y-3">
          {yearEndLoading ? (
            <div className={`flex h-20 items-center justify-center gap-2 text-sm ${muted}`}>
              <Loader2 className="animate-spin" size={16} /> Loading year-end…
            </div>
          ) : !yearEnd?.totals ? (
            <p className={`text-sm ${muted}`}>No data for {financialYear}.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  ["Declared", yearEnd.totals.declared],
                  ["Claimed", yearEnd.totals.claimed],
                  ["Approved", yearEnd.totals.approved],
                  ["Paid", yearEnd.totals.paid],
                  ["Pending approval", yearEnd.totals.pendingApproval],
                  ["Pending payment", yearEnd.totals.pendingPayment],
                  ["Taxable (claims)", yearEnd.totals.taxable],
                  ["Exempt (claims)", yearEnd.totals.exempt],
                  ["Unclaimed declared", yearEnd.totals.unclaimedDeclared],
                ].map(([label, val]) => (
                  <div key={label} className={`rounded-lg border px-2 py-2 ${border}`}>
                    <p className={`text-[10px] uppercase ${muted}`}>{label}</p>
                    <p className="text-sm font-semibold tabular-nums">
                      {typeof val === "number" && label.includes("Pending")
                        ? val
                        : `₹${Number(val || 0).toLocaleString("en-IN")}`}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                {canLedgerExport || canYearEndView ? (
                <button
                  type="button"
                  className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${border}`}
                  onClick={() => {
                    const lines = [
                      "component,claimed,approved,paid",
                      ...(yearEnd.byComponent || []).map(
                        (r) =>
                          `${r.componentCode},${r.claimed},${r.approved},${r.paid}`
                      ),
                    ];
                    downloadCsv(`reimbursement-fy-${financialYear}.csv`, lines);
                  }}
                >
                  Export component CSV
                </button>
                ) : null}
              </div>
              {(yearEnd.byComponent || []).length > 0 && (
                <div className={`overflow-auto rounded-lg border ${border}`}>
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="bg-[hsl(var(--ds-muted)/0.35)] text-left">
                        <th className="px-2 py-1.5">Component</th>
                        <th className="px-2 py-1.5 text-right">Claimed</th>
                        <th className="px-2 py-1.5 text-right">Approved</th>
                        <th className="px-2 py-1.5 text-right">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearEnd.byComponent.map((r) => (
                        <tr key={r.componentCode} className={`border-t ${border}`}>
                          <td className="px-2 py-1.5 font-medium">{r.componentCode}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{r.claimed}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{r.approved}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{r.paid}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
